import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import { HOST } from '@constants';
import type { BooleanFlag, TourData } from '@types';
import Modal from '@components/Modal';
import Title from '@components/Title';
import SvgIcon from '@components/SvgIcon';

interface PreviewData {
  author: {
    uid: string;
    nickname: string;
    avatar: string;
    cert: BooleanFlag;
    expired: BooleanFlag;
  };
  name: string;
  remark: string;
  keywords: string;
  selected: 0;
  thumbUrl: string;
  pvCount: 0;
  likeCount: 0;
  privilege: 1; // 0:不开放 1:开放 2:密码
  templateExpired: BooleanFlag;
}

interface Props {
  panoData: PreviewData;
  jsonData: Partial<TourData>;
  onRequestClose: () => void;
}

const StyledModal = styled(Modal)`
  width: calc(100% - 60px);
  height: calc(100% - 60px);
`;

const IconPC = styled(SvgIcon).attrs({
  children: ({ color }: { color: string }) => (
    <>
      <defs>
        <path
          d="M14 0a2 2 0 012 2v8a2 2 0 01-2 2h-2.333l.894 2.684A1 1 0 0111.613 16H4.387a1 1 0 01-.948-1.316L4.332 12H2a2 2 0 01-2-2V2a2 2 0 012-2h12zM9.557 13H6.443a1 1 0 00-.886.536l-.05.113-.132.351a.74.74 0 00.593.993l.1.007h3.864a.74.74 0 00.722-.904L10.625 14l-.132-.351A1 1 0 009.557 13zM13 2H3a1 1 0 00-.993.883L2 3v6a1 1 0 00.883.993L3 10h10a1 1 0 00.993-.883L14 9V3a1 1 0 00-1-1z"
          id="p-pc_svg__a"
        />
      </defs>
      <g fill="none" fillRule="evenodd">
        <mask id="p-pc_svg__b" fill="#fff">
          <use xlinkHref="#p-pc_svg__a" />
        </mask>
        <use fill={color} xlinkHref="#p-pc_svg__a" />
        <path fill={color} mask="url(#p-pc_svg__b)" d="M-1.5-2h28v28h-28z" />
      </g>
    </>
  ),
})``;

const IconMobile = styled(SvgIcon).attrs({
  children: ({ color }: { color: string }) => (
    <>
      <defs>
        <path
          d="M8 0a2 2 0 012 2v12a2 2 0 01-2 2H2a2 2 0 01-2-2V2a2 2 0 012-2h6zM7 2H3a1 1 0 00-.993.883L2 3v10a1 1 0 00.883.993L3 14h4a1 1 0 00.993-.883L8 13V3a1 1 0 00-1-1zm-.5 9a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-3a.5.5 0 01-.5-.5v-1a.5.5 0 01.5-.5h3z"
          id="p-mobile_svg__a"
        />
      </defs>
      <g transform="translate(3)" fill="none" fillRule="evenodd">
        <mask id="p-mobile_svg__b" fill="#fff">
          <use xlinkHref="#p-mobile_svg__a" />
        </mask>
        <use fill={color} xlinkHref="#p-mobile_svg__a" />
        <path
          fill={color}
          mask="url(#p-mobile_svg__b)"
          d="M-3.5 0h26v26h-26z"
        />
      </g>
    </>
  ),
})``;

function PreviewModal({ onRequestClose, panoData, jsonData }: Props) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});
  const onMessage = (msg: MessageEvent) => {
    if (msg.data === '720yun-player-ready') {
      iframeRef.current?.contentWindow?.postMessage(
        { type: '720yun-player-data', panoData, jsonData },
        '*'
      );
    }
  };
  useEffect(() => {
    const resizeHandler = () => {
      if (!containerRef.current) return;
      const height = Math.min(
        containerRef.current.clientHeight
          ? containerRef.current.clientHeight - 40
          : 0,
        720
      );
      const width = height ? (height / 16) * 9 : 0;
      setStyle({ width, height });
    };
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('resize', resizeHandler);
    };
  }, []);

  const [tab, setTab] = useState<0 | 1>(0);

  const renderHeader = () => {
    return (
      <>
        <Title>作品预览</Title>
        <div className="absolute left-1/2 -mr-1/2 flex -translate-x-1/2 transform select-none items-center">
          <div
            className={classNames(
              'flex h-30 cursor-pointer items-center justify-center rounded px-10',
              {
                'bg-blue text-white': tab === 0,
                'text-gray': tab === 1,
              }
            )}
            onClick={() => setTab(0)}
          >
            <IconPC active={tab === 0} />
            <div className="ml-5">桌面端</div>
          </div>
          <div
            className={classNames(
              'ml-10 flex h-30 w-85 cursor-pointer items-center justify-center rounded',
              {
                'bg-blue text-white': tab === 1,
                'text-gray': tab === 0,
              }
            )}
            onClick={() => setTab(1)}
          >
            <IconMobile active={tab === 1} />
            <div className="ml-5">移动端</div>
          </div>
        </div>
      </>
    );
  };

  return (
    <StyledModal isOpen onRequestClose={onRequestClose} header={renderHeader()}>
      <div
        className="absolute inset-0 flex items-center justify-center py-20"
        ref={containerRef}
      >
        {tab === 0 && (
          <iframe
            ref={iframeRef}
            title="preview"
            className="h-full w-full"
            src={`${HOST}/vr/preview`}
          />
        )}
        {tab === 1 && (
          <iframe
            ref={iframeRef}
            title="preview"
            style={style}
            src={`${HOST}/vr/preview`}
          />
        )}
      </div>
    </StyledModal>
  );
}

export default PreviewModal;
